/* Sticker Studio contour geometry. Pure functions shared by editor and tests. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.StudioCutlineCore = factory(root.ClipperLib);
})(typeof window === 'undefined' ? globalThis : window, function (Clipper) {
  'use strict';
  var SCALE = 1000;
  function area(p) {
    var a = 0;
    for (var i = 0, j = p.length - 1; i < p.length; j = i++) a += p[j][0] * p[i][1] - p[i][0] * p[j][1];
    return a / 2;
  }
  function bounds(paths) {
    var x = Infinity, y = Infinity, r = -Infinity, b = -Infinity;
    paths.forEach(function (p) { p.forEach(function (v) {
      if (!Number.isFinite(v[0]) || !Number.isFinite(v[1])) throw new Error('Non-finite cut coordinate');
      x = Math.min(x, v[0]); y = Math.min(y, v[1]); r = Math.max(r, v[0]); b = Math.max(b, v[1]);
    }); });
    return x === Infinity ? null : {left:x, top:y, width:r-x, height:b-y};
  }
  // Trace pixel *edges*, not pixel centres. Clockwise outside rings, opposite holes.
  // At a diagonal contact turn right, keeping the two closed rings separate.
  function trace(mask, width, height) {
    if (mask.length !== width * height || width < 1 || height < 1) throw new Error('Invalid mask dimensions');
    var vertices = width + 1, edges = [], starts = new Map();
    function add(x, y, nx, ny, direction) {
      if(edges.length>=100000)throw new Error('Artwork has too many tiny edges. Clean up noise or use a simpler image.');
      var start = y * vertices + x, end = ny * vertices + nx;
      var edge = {start:start, end:end, direction:direction, used:false};
      edges.push(edge);
      if (!starts.has(start)) starts.set(start, []);
      starts.get(start).push(edge);
    }
    function solid(x, y) { return x >= 0 && y >= 0 && x < width && y < height && mask[y * width + x]; }
    for (var y = 0; y < height; y++) for (var x = 0; x < width; x++) if (solid(x,y)) {
      if (!solid(x,y-1)) add(x,y,x+1,y,0);
      if (!solid(x+1,y)) add(x+1,y,x+1,y+1,1);
      if (!solid(x,y+1)) add(x+1,y+1,x,y+1,2);
      if (!solid(x-1,y)) add(x,y+1,x,y,3);
    }
    var result = [], rank = [1,0,3,2];
    edges.forEach(function (first) {
      if (first.used) return;
      var edge = first, path = [], count = 0;
      while (true) {
        if (edge.used) throw new Error('Contour revisited an edge before closing');
        edge.used = true;
        path.push([edge.start % vertices, Math.floor(edge.start / vertices)]);
        if (edge.end === first.start) break;
        var next = (starts.get(edge.end) || []).filter(function (e) { return !e.used; });
        next.sort(function (a,b) { return rank[(a.direction-edge.direction+4)%4]-rank[(b.direction-edge.direction+4)%4]; });
        if (!next.length || ++count > edges.length) throw new Error('Open contour');
        edge = next[0];
      }
      // Exact collinear compression does not change the silhouette.
      var compact = path.filter(function (p,i) {
        var a=path[(i+path.length-1)%path.length], b=path[(i+1)%path.length];
        return (p[0]-a[0])*(b[1]-p[1]) !== (p[1]-a[1])*(b[0]-p[0]);
      });
      if (compact.length >= 3 && area(compact) !== 0) result.push(compact);
    });
    return result;
  }
  function integer(paths) { return paths.map(function(p) { return p.map(function(v) { return {X:Math.round(v[0]*SCALE),Y:Math.round(v[1]*SCALE)}; }); }); }
  function decimal(paths) { return paths.map(function(p) { return p.map(function(v) { return [v.X/SCALE,v.Y/SCALE]; }); }); }
  function union(paths) {
    if (!paths.length) return [];
    var c=new Clipper.Clipper(), out=[];
    c.StrictlySimple=true;
    c.AddPaths(integer(paths),Clipper.PolyType.ptSubject,true);
    c.Execute(Clipper.ClipType.ctUnion,out,Clipper.PolyFillType.pftNonZero,Clipper.PolyFillType.pftNonZero);
    return decimal(out);
  }
  function offset(paths, amount, tolerance) {
    if (!Number.isFinite(amount)) throw new Error('Invalid contour offset');
    if (!paths.length) return [];
    if (Math.abs(amount)<0.0005) return union(paths);
    var co=new Clipper.ClipperOffset(2,Math.max(0.005,tolerance || 0.03)*SCALE), out=[];
    co.AddPaths(integer(paths),Clipper.JoinType.jtRound,Clipper.EndType.etClosedPolygon);
    co.Execute(out,amount*SCALE);
    return decimal(out);
  }
  function fromAlpha(data,width,height,options) {
    options=options || {};
    if (data.length !== width*height*4) throw new Error('Invalid RGBA buffer');
    var threshold=options.threshold === undefined ? 128 : options.threshold;
    if (threshold < 1 || threshold > 255) throw new Error('Invalid alpha threshold');
    var mask=new Uint8Array(width*height);
    for(var i=0;i<mask.length;i++) mask[i]=data[i*4+3]>=threshold?1:0;
    var paths=trace(mask,width,height), scale=options.scale || 1;
    if (!(scale>0 && Number.isFinite(scale))) throw new Error('Invalid raster scale');
    paths=paths.filter(function(p) { return options.holes || area(p)>0; }).map(function(p) {
      return p.map(function(v) { return [v[0]/scale+(options.left || 0),v[1]/scale+(options.top || 0)]; });
    });
    paths=union(paths);
    var close=options.closeRadius || 0;
    if (close<0 || !Number.isFinite(close)) throw new Error('Invalid closing radius');
    if(close>0) paths=offset(offset(paths,close),-close);
    return offset(paths,options.offset || 0,options.tolerance);
  }
  function svg(paths) {
    return paths.map(function(p) {
      if(p.length<3 || Math.abs(area(p))<1e-8) throw new Error('Degenerate cut path');
      return 'M '+p.map(function(v) { return v[0].toFixed(3)+' '+v[1].toFixed(3); }).join(' L ')+' Z';
    }).join(' ');
  }
  function pdfPaths(paths,box,width,height,margin) {
    if(!box || !(box.width>0 && box.height>0 && width>0 && height>0)) throw new Error('Invalid print dimensions');
    return paths.map(function(p) { return p.map(function(v) {
      return [(v[0]-box.left)*width/box.width+margin, height+margin-(v[1]-box.top)*height/box.height];
    }); });
  }
  return {area:area,bounds:bounds,trace:trace,union:union,offset:offset,fromAlpha:fromAlpha,svg:svg,pdfPaths:pdfPaths};
});
